import React from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import { DataGrid, gridClasses, type GridValidRowModel } from '@mui/x-data-grid';

import { TxEmpty } from './components/TxEmpty';
import { TxFooter } from './components/TxFooter';
import { useGridData } from './hooks/useGridData';
import { TxToolbar } from './components/TxToolbar';
import { formatColumns } from './utils/formatColumns';

import type { TxDataGridProps } from './types';

/** Altura aproximada da linha do DataGrid por density (para o esqueleto). */
const ALTURA_LINHA_POR_DENSIDADE = { compact: 36, standard: 52, comfortable: 68 } as const;

/**
 * Overlay de loading do modo `managed` (catálogo do canvas): esqueleto de
 * linhas + indicador em primary + barra de progresso.
 *
 * Respeita a `density` (altura das linhas do esqueleto) e o minHeight 250px do
 * modo managed. Só o modo `managed` o usa — telas com loading customizado
 * (ex.: conta-fiscal, com `EsqueletoExtrato` próprio) não passam por aqui,
 * então não há indicador duplicado.
 */
function TxLoadingOverlay({
  density = 'compact',
  label = 'Carregando autos…',
}: {
  density?: keyof typeof ALTURA_LINHA_POR_DENSIDADE;
  label?: string;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 250 }}>
      <LinearProgress />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          py: 2,
        }}
      >
        <CircularProgress size={20} color="primary" />
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, pb: 2, flex: 1 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={ALTURA_LINHA_POR_DENSIDADE[density]}
            animation="wave"
          />
        ))}
      </Box>
    </Box>
  );
}

export function TxDataGrid<T extends GridValidRowModel>(props: TxDataGridProps<T>) {
  const {
    onAdd,
    onRefresh,
    pageSizeOptions,
    density,
    columns: _columns,
    sx: userSx,
    fetchRows,
    emptyLabel,
    ...dataGridProps
  } = props;

  const defaultDensity = density ?? 'compact';

  const {
    rows,
    rowCount,
    loading,
    error,
    paginationModel,
    sortModel,
    filterModel,
    setPaginationModel,
    setSortModel,
    setFilterModel,
    reload,
  } = useGridData(props);

  const handleRefresh = onRefresh ?? reload;

  const columns = formatColumns({
    columns: _columns,
    onView: props.onView,
    onEdit: props.onEdit,
    onDelete: props.onDelete,
    actionsDisplay: props.actionsDisplay,
  });

  const effectivePageSizeOptions = React.useMemo(() => {
    const opts = Array.isArray(pageSizeOptions) ? [...pageSizeOptions] : [];
    const values = opts.map((o) => (typeof o === 'number' ? o : o.value));
    if (!values.includes(paginationModel.pageSize)) {
      opts.push(paginationModel.pageSize);
    }
    return opts;
  }, [pageSizeOptions, paginationModel.pageSize]);

  // Memoiza cada slot para a toolbar/overlays não “piscar” a cada render
  const toolbarSlot = React.useMemo(
    () => () => <TxToolbar onAdd={onAdd} onRefresh={handleRefresh} />,
    [onAdd, handleRefresh]
  );
  const loadingOverlaySlot = React.useCallback(
    () => <TxLoadingOverlay density={defaultDensity} />,
    [defaultDensity]
  );
  const noRowsOverlaySlot = React.useMemo(
    () => () => <TxEmpty title={emptyLabel} error={error ?? undefined} />,
    [error, emptyLabel]
  );
  const noResultsOverlaySlot = React.useMemo(
    () => () => <TxEmpty title="Nenhum resultado encontrado" />,
    []
  );

  const slots = {
    loadingOverlay: loadingOverlaySlot,
    toolbar: toolbarSlot,
    footer: TxFooter,
    noRowsOverlay: noRowsOverlaySlot,
    noResultsOverlay: noResultsOverlaySlot,
  } as const;

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <DataGrid<T>
        {...dataGridProps}
        density={defaultDensity}
        rows={rows}
        columns={columns}
        loading={loading}
        pagination
        paginationMode={fetchRows ? 'server' : 'client'}
        sortingMode={fetchRows ? 'server' : 'client'}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        /** 📌 Aqui entram os campos de filtro: */
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        rowCount={fetchRows ? rowCount : undefined}
        pageSizeOptions={effectivePageSizeOptions}
        slots={slots}
        slotProps={{
          footer: { hideToolbar: false, hidePageSizeSelector: false } as any,
        }}
        sx={{
          [`& .${gridClasses.cell}`]: { alignItems: 'center', display: 'inline-flex' },
          '& .MuiDataGrid-virtualScroller': { pt: 0, minHeight: '250px' },
          ...userSx,
        }}
      />
    </Paper>
  );
}
